import React, { useEffect } from "react";
import { SearchModal } from "~/widgets/SearchModal";
import { useFocusContext } from "~/contexts/FocusContext";
import { computeHpMax, computeResilienceMax } from "~/utils/characterDerived";
import styles from "~/widgets/CharacterSearch/CharacterSearch.module.scss";
import type { CharacterData } from "~/types/storage";

export interface CharacterSearchProps {
  isOpen: boolean;
  onClose: () => void;
  characters: CharacterData[];
  activeCharacterId: string;
  onSelect: (characterId: string) => void;
}

/**
 * CharacterSearch Component
 *
 * A floating search modal that allows users to quickly find and switch between characters.
 *
 * Features:
 * - Keyboard navigation (up/down arrows, enter, escape)
 * - Fuzzy search by character name
 * - Shows character stats preview
 */
const CharacterSearch: React.FC<CharacterSearchProps> = ({
  isOpen,
  onClose,
  characters,
  activeCharacterId,
  onSelect,
}) => {
  const { setIsOverlayOpen } = useFocusContext();

  // Notify FocusContext when overlay opens/closes
  useEffect(() => {
    setIsOverlayOpen(isOpen);
  }, [isOpen, setIsOverlayOpen]);

  const filterFn = (char: CharacterData, query: string): boolean => {
    return char.name.toLowerCase().includes(query);
  };

  const handleSelect = (char: CharacterData) => {
    onSelect(char.id);
  };

  const renderItem = (char: CharacterData, isSelected: boolean) => {
    const isActive = char.id === activeCharacterId;
    const mind = char.coreStats["Mind"] ?? 0;
    const body = char.coreStats["Body"] ?? 0;
    const reflex = char.coreStats["Reflex"] ?? 0;
    const hpMax = computeHpMax(char.coreStats);
    const resMax = computeResilienceMax(char.coreStats);

    return (
      <div className={styles.characterInfo}>
        <div className={styles.nameRow}>
          <span
            className={`${styles.characterName} ${isSelected ? styles.characterNameSelected : ""} ${isActive ? styles.characterNameActive : ""}`}
          >
            {char.name}
          </span>
          {isActive && (
            <span className={styles.activeBadge}>Active</span>
          )}
        </div>
        <div className={styles.statsRow}>
          <span className={styles.stat}>M:{mind}</span>
          <span className={styles.stat}>B:{body}</span>
          <span className={styles.stat}>R:{reflex}</span>
          <span className={styles.statDivider}>|</span>
          <span className={styles.stat}>HP:{char.currentHp}/{hpMax}</span>
          <span className={styles.stat}>Res:{char.currentResilience}/{resMax}</span>
          {char.strain > 0 && (
            <span className={styles.strainStat}>Strain:{char.strain}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <SearchModal<CharacterData>
      isOpen={isOpen}
      onClose={onClose}
      placeholder="Search characters..."
      items={characters}
      getItemKey={(char) => char.id}
      filterFn={filterFn}
      renderItem={renderItem}
      onSelect={handleSelect}
      emptyMessage="No characters found"
      emptyHint="Try a different search term"
      footerHints={[
        { label: "navigate", hint: "↑↓" },
        { label: "select", hint: "↵" },
        { label: "to open", hint: "C" },
      ]}
    />
  );
};

export default CharacterSearch;
